// =====================================================
// AuthContext — fuente única de verdad de la sesión real
//
// Mantiene session, user, profile, roles y aal (nivel MFA).
// Los roles SIEMPRE se leen desde la base de datos (user_roles),
// nunca desde localStorage. El frontend solo oculta UI por comodidad;
// la seguridad real vive en las políticas RLS de Supabase.
// =====================================================
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../integrations/supabase/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [aal, setAal] = useState(null); // { currentLevel, nextLevel }
  const [loading, setLoading] = useState(true);
  // true tras abrir un enlace de recuperación: hay que fijar contraseña nueva
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  // Carga perfil + roles desde la BD (diferida para no bloquear el callback de auth)
  const loadProfileAndRoles = useCallback(async (uid) => {
    if (!uid) { setProfile(null); setRoles([]); return; }
    const [{ data: prof }, { data: roleRows }] = await Promise.all([
      supabase.from('profiles').select('*').eq('user_id', uid).maybeSingle(),
      supabase.from('user_roles').select('role').eq('user_id', uid),
    ]);
    setProfile(prof || null);
    setRoles((roleRows || []).map((r) => r.role));
  }, []);

  const refreshAal = useCallback(async () => {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      setAal(data || null);
    } catch (e) {
      setAal(null);
    }
  }, []);

  useEffect(() => {
    // 1) Listener primero (síncrono), datos diferidos con setTimeout(0)
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      if (event === 'SIGNED_OUT') setPasswordRecovery(false);
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        setTimeout(() => {
          loadProfileAndRoles(newSession.user.id);
          refreshAal();
        }, 0);
      } else {
        setProfile(null);
        setRoles([]);
        setAal(null);
      }
    });

    // 2) Sesión existente
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        loadProfileAndRoles(existing.user.id);
        refreshAal();
      }
      setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, [loadProfileAndRoles, refreshAal]);

  const signOut = useCallback(async () => {
    try { await supabase.auth.signOut(); } catch (e) {}
    // Limpieza total en memoria para no dejar datos residuales
    setSession(null);
    setUser(null);
    setProfile(null);
    setRoles([]);
    setAal(null);
    setPasswordRecovery(false);
  }, []);

  const hasRole = useCallback((role) => roles.includes(role), [roles]);
  const isAdmin = roles.includes('admin');

  // aal2 requerido pero no alcanzado => hay un challenge MFA pendiente
  const mfaPending =
    !!aal && aal.nextLevel === 'aal2' && aal.currentLevel === 'aal1';

  // El admin crea la cuenta con una contraseña temporal y marca este flag.
  // Hasta que el usuario fije su propia contraseña, debe pasar por /change-password.
  const mustChangePassword = !!user?.user_metadata?.must_change_password;

  const value = {
    session,
    user,
    profile,
    roles,
    aal,
    loading,
    mfaPending,
    mustChangePassword,
    passwordRecovery,
    clearPasswordRecovery: () => setPasswordRecovery(false),
    isAdmin,
    hasRole,
    signOut,
    refreshAal,
    reloadProfile: () => user && loadProfileAndRoles(user.id),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
